import { api } from './api'

/**
 * The social section (B6): consent, sharing, the feed, reactions,
 * following, the leaderboard. Every read is scoped by the server's privacy
 * switch — until the owner turns it on, the feed is the learner's own
 * posts — and a shared recording is reached only through `fetchPostAudio`,
 * which the server gates by who may hear it.
 */

export async function socialStatus() {
  const { data } = await api.get('/social/status')
  return data
}

/** Agreeing to the current consent text; `false` withdraws and removes every post of the caller. */
export async function updateConsent(agree) {
  const { data } = await api.post('/social/update-consent', { agree })
  return data
}

/** Shares one of the caller's scored attempts; sharing it again answers the same post. */
export async function sharePost(attemptId) {
  const { data } = await api.post('/social/share', { attemptId })
  return data
}

export async function unsharePost(postId) {
  const { data } = await api.post('/social/unshare', { postId })
  return data
}

/** `scope` is `following` · `teammates` (V5) · `top` · `new`; `cursor` is the previous page's `nextCursor`. */
export async function feed({ scope = 'new', cursor, limit } = {}) {
  const { data } = await api.get('/social/feed', { params: { scope, cursor, limit } })
  return data
}

/**
 * A learner's public profile (V5-b `social_profile`): the feed's author
 * fields, `periodXp`, `practiceCount`, `badges`, and their posts a page at a
 * time under the feed's own privacy rule (none of someone else's while the
 * feed is not public). 404 `user_not_found` — also for a deactivated account.
 */
export async function publicProfile(userId, { cursor, limit } = {}) {
  const { data } = await api.get(`/social/profile/${encodeURIComponent(userId)}`, { params: { cursor, limit } })
  return data
}

/**
 * The recording behind a post, as an object URL. `audioUrl` is the
 * relative path the feed hands out (`social/audio/{id}`); the caller owns
 * the URL and revokes it when done.
 */
export async function fetchPostAudio(audioUrl) {
  const { data } = await api.get(audioUrl, { responseType: 'blob' })
  return URL.createObjectURL(data)
}

export async function clapPost(postId, on) {
  const { data } = await api.post('/social/clap', { postId, on })
  return data
}

export async function ratePost(postId, stars) {
  const { data } = await api.post('/social/rate', { postId, stars })
  return data
}

export async function commentPost(postId, text) {
  const { data } = await api.post('/social/comment', { postId, text })
  return data
}

export async function followUser(userId, on) {
  const { data } = await api.post('/social/follow', { userId, on })
  return data
}

/**
 * `period` is `weekly` (the last 7 days) or `monthly` (the last 30) — or,
 * V6-b-leaderboard, `week` / `month` (the calendar boards) with
 * `options.category` (`xp` · `team` · `improved` · `popular` · `teams`) and
 * `options.periodKey`. Without options the request is exactly the legacy one.
 */
export async function leaderboard(period = 'weekly', options) {
  const params = { period }
  if (options?.category) params.category = options.category
  if (options?.periodKey) params.periodKey = options.periodKey
  const { data } = await api.get('/social/leaderboard', { params })
  return data
}
